export type JsonLdValue = string | number | boolean | null | undefined | JsonLdObject | JsonLdValue[]

export type JsonLdObject = {
  [key: string]: JsonLdValue
}

export type SiteIdentity = {
  name: string
  url?: string
  description?: string
  logo?: string
  sameAs?: string[]
  telephone?: string
  email?: string
}

export type PostalAddressInput = {
  streetAddress?: string
  addressLocality?: string
  addressRegion?: string
  postalCode?: string
  addressCountry?: string
}

export type ReviewInput = {
  author: string
  reviewBody: string
  ratingValue?: number
  bestRating?: number
}

export type FaqInput = {
  question: string
  answer: string
}

export type OfferInput = {
  name: string
  price?: string | number
  priceCurrency?: string
  url?: string
  description?: string
}

export function compactJsonLd<T extends JsonLdObject>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry === undefined || entry === null || entry === "") return false
      if (Array.isArray(entry) && entry.length === 0) return false
      return true
    }),
  ) as T
}

export function buildOrganizationSchema(identity: SiteIdentity): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: identity.name,
    url: identity.url,
    description: identity.description,
    logo: identity.logo,
    sameAs: identity.sameAs,
    contactPoint: identity.telephone || identity.email ? compactJsonLd({
      "@type": "ContactPoint",
      telephone: identity.telephone,
      email: identity.email,
      contactType: "customer support",
    }) : undefined,
  })
}

export function buildSiteNavigationSchema(items: { name: string; url: string }[]): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item, index) => compactJsonLd({
      "@type": "SiteNavigationElement",
      position: index + 1,
      name: item.name,
      url: item.url,
    })),
  })
}

export function buildBreadcrumbSchema(items: { name: string; url: string }[]): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => compactJsonLd({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  })
}

export function buildFaqSchema(items: FaqInput[]): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => compactJsonLd({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: compactJsonLd({
        "@type": "Answer",
        text: item.answer,
      }),
    })),
  })
}

export function buildHowToSchema(input: { name: string; description?: string; steps: { name: string; text: string }[] }): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    step: input.steps.map((step, index) => compactJsonLd({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  })
}

export function buildArticleSchema(input: {
  headline: string
  description?: string
  image?: string
  datePublished?: string
  dateModified?: string
  authorName?: string
  url?: string
}): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.headline,
    description: input.description,
    image: input.image,
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    url: input.url,
    author: input.authorName ? compactJsonLd({
      "@type": "Person",
      name: input.authorName,
    }) : undefined,
  })
}

export function buildLocalBusinessSchema(input: SiteIdentity & { address?: PostalAddressInput; openingHours?: string[] }): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: input.name,
    url: input.url,
    description: input.description,
    logo: input.logo,
    telephone: input.telephone,
    email: input.email,
    openingHours: input.openingHours,
    address: input.address ? compactJsonLd({
      "@type": "PostalAddress",
      ...input.address,
    }) : undefined,
  })
}

export function buildProductSchema(input: {
  name: string
  description?: string
  image?: string
  offers?: OfferInput[]
  ratingValue?: number
  reviewCount?: number
}): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    image: input.image,
    aggregateRating: input.ratingValue && input.reviewCount ? compactJsonLd({
      "@type": "AggregateRating",
      ratingValue: input.ratingValue,
      reviewCount: input.reviewCount,
    }) : undefined,
    offers: input.offers?.map((offer) => compactJsonLd({
      "@type": "Offer",
      name: offer.name,
      price: offer.price,
      priceCurrency: offer.priceCurrency,
      url: offer.url,
      description: offer.description,
    })),
  })
}

export function buildReviewSchema(input: ReviewInput & { itemReviewedName: string }): JsonLdObject {
  return compactJsonLd({
    "@context": "https://schema.org",
    "@type": "Review",
    reviewBody: input.reviewBody,
    author: compactJsonLd({
      "@type": "Person",
      name: input.author,
    }),
    itemReviewed: compactJsonLd({
      "@type": "Thing",
      name: input.itemReviewedName,
    }),
    reviewRating: input.ratingValue ? compactJsonLd({
      "@type": "Rating",
      ratingValue: input.ratingValue,
      bestRating: input.bestRating ?? 5,
    }) : undefined,
  })
}
